import React, { useState } from 'react';
import { IconButton, Tooltip, CircularProgress } from '@mui/material';
import {
  PlayArrow as PlayIcon,
  CheckCircle as CheckInIcon,
  MedicalServices as ConsultationIcon,
  Done as CompleteIcon,
  Schedule as WaitingIcon,
  Phone as CalledIcon,
  PersonAdd as WalkInIcon,
  EventAvailable as ConfirmIcon
} from '@mui/icons-material';
import { appointmentServiceInstance } from '../services/appointmentTimelineService'; // Importar el service

const PlayButtonComponent = ({ 
  appointment, 
  onStatusChange,
  disabled = false 
}) => {
  const [isLoading, setIsLoading] = useState(false);

  // Flujo de estados con métodos específicos del AppointmentService
  const statusFlow = {
    'Pending': {
      next: 'Confirmed',
      action: 'Confirmar',
      method: 'confirmAppointment', // Método específico del service
      icon: <ConfirmIcon sx={{ fontSize: 14 }} />,
      color: '#1976d2',
      hoverColor: '#1565c0'
    },
    'Confirmed': {
      next: 'Waiting',
      action: 'Check-in',
      method: 'checkinPatient', // Método específico del service
      icon: <CheckInIcon sx={{ fontSize: 14 }} />,
      color: '#2e7d32',
      hoverColor: '#1b5e20'
    },
    'Waiting': {
      next: 'Called',
      action: 'Llamar',
      method: 'updateAppointmentStatus', // Método genérico para este caso específico
      params: { status: 'Called' }, // Parámetros adicionales
      icon: <CalledIcon sx={{ fontSize: 14 }} />,
      color: '#ff9800',
      hoverColor: '#f57c00'
    },
    'Called': {
      next: 'In Consultation',
      action: 'Iniciar Consulta',
      method: 'startConsultation', // Método específico del service
      icon: <ConsultationIcon sx={{ fontSize: 14 }} />,
      color: '#9c27b0',
      hoverColor: '#7b1fa2'
    },
    'In Consultation': {
      next: 'Completed',
      action: 'Completar',
      method: 'completeConsultation', // Método específico del service
      icon: <CompleteIcon sx={{ fontSize: 14 }} />,
      color: '#4caf50',
      hoverColor: '#388e3c'
    },
    'Walk-in': {
      next: 'Waiting',
      action: 'Check-in',
      method: 'checkinPatient', // Método específico del service
      icon: <CheckInIcon sx={{ fontSize: 14 }} />,
      color: '#ff5722',
      hoverColor: '#d84315'
    },
    'Rescheduled': {
      next: 'Confirmed',
      action: 'Confirmar',
      method: 'confirmAppointment', // Método específico del service
      icon: <ConfirmIcon sx={{ fontSize: 14 }} />,
      color: '#607d8b',
      hoverColor: '#455a64'
    }
  };

  // Función mejorada que usa el AppointmentService
  const changeAppointmentStatus = async (mongoId, statusConfig, notes = null) => {
    try {
      setIsLoading(true);
      
      const { next: newStatus, method, params = {} } = statusConfig;
      
      console.log('🔄 Iniciando cambio de estado con AppointmentService:', {
        mongoId,
        appointmentId: appointment.appointmentId,
        patientName: appointment.patientName,
        currentStatus: appointment.status,
        nextStatus: newStatus,
        method,
        params,
        fullAppointment: appointment
      });

      // 🔍 PASO 1: Verificar que la cita existe
      console.log('🔍 PASO 1: Verificando existencia de la cita...');
      try {
        const checkResponse = await appointmentServiceInstance.getAppointmentById(mongoId);
        if (!checkResponse.success || !checkResponse.data) {
          throw new Error(`Cita no encontrada: ${checkResponse.message || 'Sin datos'}`);
        }
        console.log('✅ Cita encontrada:', checkResponse.data);
      } catch (checkError) {
        console.error('❌ Error verificando cita:', checkError);
        throw new Error(`Error al verificar la cita: ${checkError.message}`);
      }

      // 🔄 PASO 2: Ejecutar la acción específica usando el método del service
      console.log(`🔄 PASO 2: Ejecutando ${method} para cambiar estado...`);
      
      let result;
      
      switch (method) {
        case 'confirmAppointment':
          result = await appointmentServiceInstance.confirmAppointment(mongoId, { notes });
          break;
          
        case 'checkinPatient':
          result = await appointmentServiceInstance.checkinPatient(mongoId);
          break;
          
        case 'startConsultation':
          result = await appointmentServiceInstance.startConsultation(mongoId);
          break;
          
        case 'completeConsultation':
          result = await appointmentServiceInstance.completeConsultation(mongoId, notes);
          break;
          
        case 'updateAppointmentStatus':
          // Para casos específicos como 'Called' que usan el método genérico
          const statusToUpdate = params.status || newStatus;
          result = await appointmentServiceInstance.updateAppointmentStatus(mongoId, statusToUpdate, notes);
          break;
          
        case 'markAsWalkIn':
          result = await appointmentServiceInstance.markAsWalkIn(mongoId, { notes });
          break;
          
        case 'rescheduleAppointment':
          // Este caso requiere datos específicos de fecha/hora
          result = await appointmentServiceInstance.rescheduleAppointment(mongoId, { notes, ...params });
          break;
          
        case 'cancelAppointment':
          result = await appointmentServiceInstance.cancelAppointment(mongoId, { notes, reason: 'Cancelled by staff' });
          break;
          
        case 'markAsNoShow':
          result = await appointmentServiceInstance.markAsNoShow(mongoId, { notes });
          break;
          
        default:
          throw new Error(`Método no reconocido: ${method}`);
      }
      
      console.log('📨 Response del service:', result);
      
      if (result.success && result.data) {
        console.log('✅ Estado actualizado exitosamente:', result.data);
        
        // Transformar la respuesta del backend para la UI
        if (onStatusChange) {
          const transformedAppointment = {
            id: result.data._id || mongoId,
            appointmentId: result.data.appointmentId || appointment.appointmentId || '',
            patientName: result.data.patientName || appointment.patientName || '',
            patientAvatar: result.data.patientAvatar || appointment.patientAvatar || '',
            dateOfBirth: result.data.dateOfBirth ? new Date(result.data.dateOfBirth).toISOString().split('T')[0] : appointment.dateOfBirth || '',
            address: result.data.address || appointment.address || '',
            userid: result.data.userid || appointment.userid || '',
            meetinguserid: result.data.meetinguserid || appointment.meetinguserid || '',
            doctorName: result.data.doctorName || appointment.doctorName || '',
            doctorRole: result.data.doctorRole || appointment.doctorRole || '',
            date: result.data.date ? new Date(result.data.date).toISOString().split('T')[0] : appointment.date || '',
            startTime: result.data.startTime || appointment.startTime || '',
            endTime: result.data.endTime || appointment.endTime || '',
            status: result.data.status || newStatus,
            medicalHistory: result.data.medicalHistory || appointment.medicalHistory || '',
            appointmentType: result.data.appointmentType || appointment.appointmentType || '',
            notes: result.data.notes || appointment.notes || '',
            checkInTime: result.data.checkInTime ? new Date(result.data.checkInTime).toISOString() : appointment.checkInTime || '',
            waitTime: result.data.waitTime || appointment.waitTime || 0,
            consultationDuration: result.data.consultationDuration || appointment.consultationDuration || 0,
            isaccepted: result.data.isaccepted !== undefined ? result.data.isaccepted : appointment.isaccepted || false,
            createdAt: result.data.createdAt ? new Date(result.data.createdAt).toISOString().split('T')[0] : appointment.createdAt || '',
            updatedAt: new Date().toISOString().split('T')[0]
          };
          
          console.log('🔄 Enviando appointment transformado al callback:', transformedAppointment);
          onStatusChange(transformedAppointment);
        }
        
        return result.data;
      } else {
        console.error('❌ Error al actualizar estado:', result.message);
        throw new Error(result.message || 'Error desconocido del servidor');
      }
      
    } catch (error) {
      console.error('❌ Error en changeAppointmentStatus:', error);
      
      // Mostrar alertas más informativas según el tipo de error
      if (error.message.includes('not found') || error.message.includes('no encontrada')) {
        alert(`❌ Cita no encontrada\n\n` +
              `ID: ${mongoId}\n` +
              `Paciente: ${appointment.patientName}\n\n` +
              `La cita puede haber sido eliminada o el ID es incorrecto.`);
      } else if (error.message.includes('Network') || error.message.includes('fetch')) {
        alert(`❌ Error de conexión\n\n` +
              `No se pudo conectar al servidor.\n` +
              `Verifica que el servidor esté ejecutándose.`);
      } else if (error.message.includes('JSON') || error.message.includes('parsing')) {
        alert(`❌ Error de formato de respuesta\n\n` +
              `El servidor retornó una respuesta inválida.\n` +
              `Revisa los logs del servidor.`);
      } else {
        alert(`❌ Error: ${error.message}\n\n` +
              `Método: ${statusConfig.method}\n` +
              `Estado actual: ${appointment.status}\n` +
              `Estado destino: ${statusConfig.next}\n\n` +
              `Revisa la consola para más detalles.`);
      }
      
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Test de conectividad usando el AppointmentService
  const testServerConnection = async () => {
    console.log('🔍 [TEST] Probando conectividad con AppointmentService...');
    
    try {
      // Test básico de conectividad
      const testResponse = await appointmentServiceInstance.getAllAppointments({ limit: 1 });
      console.log('✅ [TEST] Servidor conectado correctamente:', testResponse.success);
      
      return testResponse.success;
    } catch (error) {
      console.error('❌ [TEST] Error de conectividad:', error.message);
      return false;
    }
  };

  // Manejar el click del botón
  const handleClick = async (e) => {
    e.stopPropagation();
    
    if (disabled || isLoading) return;

    // Test de conectividad
    const isServerConnected = await testServerConnection();
    if (!isServerConnected) {
      alert('❌ No se puede conectar al servidor.\n\nVerifica que el servidor esté ejecutándose y vuelve a intentar.');
      return;
    }

    const currentStatus = appointment.status;
    const statusConfig = statusFlow[currentStatus];
    
    if (!statusConfig) {
      console.warn(`❌ No hay transición definida para el estado: ${currentStatus}`);
      alert(`No se puede procesar la cita desde el estado: "${currentStatus}"\n\nEstados válidos: ${Object.keys(statusFlow).join(', ')}`);
      return;
    }

    const mongoId = appointment.id;
    
    if (!mongoId) {
      console.error('❌ No se encontró MongoDB _id de la cita:', {
        appointment,
        hasId: !!appointment.id,
        hasAppointmentId: !!appointment.appointmentId,
        appointmentKeys: Object.keys(appointment)
      });
      alert('❌ Error: No se pudo identificar la cita.\n\nEl registro no tiene un ID válido. Por favor, recarga la página.');
      return;
    }

    const objectIdRegex = /^[0-9a-fA-F]{24}$/;
    if (!objectIdRegex.test(mongoId)) {
      console.error('❌ ID de MongoDB inválido:', mongoId);
      alert(`❌ Error: ID inválido "${mongoId}"\n\nEl ID debe ser un ObjectId válido de MongoDB (24 caracteres hexadecimales).`);
      return;
    }

    try {
      const notes = `${statusConfig.action} - ${new Date().toLocaleString('es-ES')}`;
      
      console.log('🔄 Iniciando cambio de estado:', {
        mongoId,
        appointmentId: appointment.appointmentId,
        patientName: appointment.patientName,
        currentStatus,
        nextStatus: statusConfig.next,
        method: statusConfig.method,
        action: statusConfig.action
      });
      
      await changeAppointmentStatus(mongoId, statusConfig, notes);
      
    } catch (error) {
      console.error('❌ Error al cambiar estado:', error);
      // El error ya se maneja en changeAppointmentStatus
    }
  };

  // Resto del componente permanece igual
  const currentStatus = appointment.status;
  const config = statusFlow[currentStatus];
  
  console.log(`🔍 PlayButton para ${appointment.patientName}:`, {
    currentStatus,
    hasConfig: !!config,
    appointmentId: appointment.appointmentId,
    mongoId: appointment.id,
    method: config?.method,
    nextStatus: config?.next
  });
  
  if (!config) {
    console.log(`ℹ️ No hay botón disponible para el estado: ${currentStatus}`);
    return null;
  }

  if (['Completed', 'Cancelled', 'No Show'].includes(currentStatus)) {
    console.log(`ℹ️ Estado final alcanzado: ${currentStatus}`);
    return null;
  }

  return (
    <Tooltip 
      title={`${config.action} cita - ${appointment.patientName} (${config.method})`} 
      arrow
      placement="top"
    >
      <span>
        <IconButton
          size="small"
          disabled={disabled || isLoading}
          sx={{ 
            backgroundColor: config.color,
            color: 'white',
            '&:hover': { 
              backgroundColor: config.hoverColor 
            },
            '&:disabled': {
              backgroundColor: '#ccc',
              color: '#666'
            },
            width: 24,
            height: 24,
            position: 'relative',
            margin: '2px'
          }}
          onClick={handleClick}
        >
          {isLoading ? (
            <CircularProgress 
              size={12} 
              sx={{ color: 'white' }} 
            />
          ) : (
            config.icon
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default PlayButtonComponent;